/**
 * Ответ API → снимок для сводного списка.
 *
 * Страница презентационная: печатает готовые величины и не знает ни про
 * стадии сборки, ни про то, что балл лежит внутри снимка. Перевод живёт
 * здесь — как и для хранилища одного проекта (`CompanyArchive`).
 *
 * @format
 */

import type {OverviewReport, ReportsOverviewData} from 'ui';

import {
  ERROR_REASON_LABEL,
  STATUS_LABEL,
  type ReportErrorReason,
  type ReportStatus,
} from '../../content';

/** Полосы шкалы балла — те же коды, что в схеме и в словаре вердиктов. */
type ScoreZone = NonNullable<OverviewReport['scoreZone']>;

export interface RawOverviewReport {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  generatedAt?: string | null;
  errorReason?: string | null;
  company?: {id: string; name: string} | null;
  reportData?: {
    project?: {score?: number | null; scoreZone?: string | null} | null;
  } | null;
}

/**
 * Стадия сборки → состояние строки. Словарь общий с хранилищем: одна и та
 * же стадия обязана называться в двух списках одинаково.
 */
const STATUS: Record<ReportStatus, OverviewReport['status']> = {
  READY: 'ready',
  COLLECTING: 'building',
  GENERATING: 'building',
  PENDING: 'scheduled',
  ERROR: 'failed',
};

/**
 * Тот же словарь, но признающий, что стадия может оказаться незнакомой:
 * сервер расширяет перечисление раньше, чем про это узнаёт фронт, а
 * `undefined` в строке роняет её на `status.tone`.
 */
const KNOWN_STATUS: {readonly [key: string]: OverviewReport['status'] | undefined} = STATUS;

const KNOWN_ZONES: readonly string[] = ['URGENT', 'NEEDS_ATTENTION', 'NORMAL', 'EXCELLENT'];

/**
 * Полоса шкалы. Незнакомый код отбрасываем: вердикта для него нет, а
 * подставлять соседний — врать про кабинет.
 */
function zone(value: string | null | undefined): ScoreZone | undefined {
  return value != null && KNOWN_ZONES.includes(value) ? (value as ScoreZone) : undefined;
}

/**
 * Проект без имени — законный случай: доступ к проекту могли отозвать
 * между сборкой отчёта и открытием списка. Строка остаётся, имя заменяет
 * прочерк — молча пропавший отчёт хуже, чем отчёт без подписи.
 */
const UNKNOWN_COMPANY = 'Проект недоступен';

/**
 * Свежие сверху. Порядок из API не документирован, а группировка на
 * странице его сохраняет — значит задать порядок должен маппер.
 */
function newestFirst(a: RawOverviewReport, b: RawOverviewReport): number {
  if (a.periodStart !== b.periodStart) return a.periodStart < b.periodStart ? 1 : -1;
  return (b.generatedAt ?? '').localeCompare(a.generatedAt ?? '');
}

export function toOverviewData(reports: readonly RawOverviewReport[]): ReportsOverviewData {
  const sorted = [...reports].sort(newestFirst);

  return {
    reports: sorted.map((report): OverviewReport => {
      const project = report.reportData?.project;
      const reason = report.errorReason as ReportErrorReason | null | undefined;

      return {
        id: report.id,
        companyId: report.company?.id ?? report.id,
        companyName: report.company?.name ?? UNKNOWN_COMPANY,
        periodFrom: report.periodStart,
        periodTo: report.periodEnd,
        // Незнакомая стадия вероятнее всего новая промежуточная —
        // «Собирается» самое честное, что можно сказать, не зная кода.
        status: KNOWN_STATUS[report.status] ?? 'building',
        // Подпись точнее состояния: «Собираем данные» и «Готовим выводы»
        // рисуются одинаково, но называются по-разному. Незнакомую стадию
        // не подписываем вовсе — пусть говорит состояние.
        statusLabel: STATUS_LABEL[report.status as ReportStatus],
        generatedAt: report.generatedAt ?? undefined,
        score: project?.score ?? undefined,
        scoreZone: zone(project?.scoreZone),
        note: reason ? ERROR_REASON_LABEL[reason] : undefined,
      };
    }),
  };
}
